import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { styles } from './common';
import { useLoginState, validToken } from './LoginContext';
import { KizenTypography } from '__app/typography';
import { useDispatch } from 'react-redux';
import Icon from '__components/Kizen/Icon';
import { IconSizing } from '__components/Kizen/Icon';

import { serviceTokenLoginStart } from 'store/authentication/authenticationAction';

import AuthForm from '__components/Kizen/AuthForm';
import Input from '__components/Kizen/Input';
import Button from '__components/Button';
import useFormSubmit from '__hooks/useFormSubmit';
import loginCounter from './LoginCounter';
import { BasicCheckbox } from '__components/Checkbox/BasicCheckbox';

import {
  MfaCheckBoxWrapper,
  MfaCheckBoxLabel,
  MfaCheckBoxIconWrapper,
  LinkButton,
} from './styles';
import { useTooltip } from '__components/Kizen/Tooltip';
import { useCallback, useState } from 'react';
import { TextButton } from './styles';
import AuthenticationService from 'services/AuthenticationService';
import { toastVariant, useToast } from '__components/ToastProvider';
import { getErrorMessage } from '__hooks/useErrors';
import { getOriginalError } from '__services/AxiosService';
import { AxiosError } from 'axios';

type MfaCheckBoxProps = {
  label: string;
  tooltipLabel: string;
  checked: boolean;
  onChange: () => void;
};

const MfaCheckBox = (props: MfaCheckBoxProps) => {
  const { label, tooltipLabel, checked, onChange } = props;

  const [tooltipProps, tooltip] = useTooltip(
    {
      label: tooltipLabel,
      placement: 'top',
      popperConfig: {
        modifiers: {
          preventOverflow: { enabled: false },
        },
      },
    },
    () => true
  );
  return (
    <MfaCheckBoxWrapper>
      <MfaCheckBoxIconWrapper>
        <BasicCheckbox
          size="small"
          checked={checked}
          onChange={onChange}
          withIcon
        />
      </MfaCheckBoxIconWrapper>
      <MfaCheckBoxLabel>{label}</MfaCheckBoxLabel>
      <IconSizing size="10px">
        <Icon icon="info-circle" {...tooltipProps} />
      </IconSizing>
      {tooltip}
    </MfaCheckBoxWrapper>
  );
};

export const MfaCode = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showToast] = useToast();

  const {
    email,
    setEmail,
    mfaToken,
    setMfaToken,
    setPassword,
    mfaRemember,
    setMfaRemember,
    mfaOption,
    setMfaOption,
  } = useLoginState();

  const [submitState, fetchSubmit] = useFormSubmit(async () => {
    loginCounter.count(email);
    await dispatch(
      serviceTokenLoginStart(email, mfaOption, mfaToken, mfaRemember)
    );
  }, [mfaToken, mfaRemember]) as [{ loading: boolean }, any];

  const handleMfaToggle = useCallback(() => {
    setMfaRemember(!mfaRemember);
  }, [mfaRemember, setMfaRemember]);

  const onClickSendEmail = async () => {
    setSendingEmail(true);
    try {
      const { detail } = await AuthenticationService.getOTPTokenViaEmail();
      setMfaOption('email');
      setMfaToken('');
      showToast({
        message: detail,
        variant: toastVariant.SUCCESS,
      });
    } catch (e) {
      let postMessage = '';
      if (
        e instanceof AxiosError &&
        e.response?.status === 429 &&
        e.response.headers['retry-after']
      ) {
        postMessage = ` ${t('({{retryAfter}} seconds remaining)', { retryAfter: e.response.headers['retry-after'] })}`;
      }

      const message = getErrorMessage(getOriginalError(e));
      showToast({
        message:
          (message || t('Failed to send email OTP. Try again later.')) +
          postMessage,
        variant: toastVariant.FAILURE,
      });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <AuthForm styles={styles} isShowInactivityMessage={false}>
      <div
        className={classNames({
          'Authform-wrapper': true,
          'Authform-wrapper-with-message': false,
        })}
      >
        <KizenTypography
          type="header"
          className="Authform-header"
          data-qa="login-header"
        >
          {t('Login to Kizen')}
        </KizenTypography>

        <Input
          value={email}
          label={t('Email Address')}
          type={Input.INPUT_TYPES.EMAIL}
          required
          className="Authform-input"
          disabled
        />
        <div>
          <KizenTypography
            type="subheader"
            className="Authform-subheader"
            data-qa="verify-your-identity"
          >
            {t('Verify Your Identity')}
          </KizenTypography>
        </div>
        <div
          onKeyDown={(ev) => {
            if (ev.code === 'Enter' && validToken(mfaToken)) {
              fetchSubmit();
            }
          }}
        >
          <Input
            value={mfaToken}
            label={t('Code from {{mfaOption}}', {
              mfaOption:
                mfaOption === 'totp' ? t('Authenticator App') : t('Email'),
            })}
            className="Authform-password"
            required
            onChange={setMfaToken}
            placeholder={t('Enter Code')}
            type={Input.INPUT_TYPES.ONLY_NUMBER}
            data-qa="mfa-code"
          />
          <MfaCheckBox
            checked={mfaRemember}
            onChange={handleMfaToggle}
            data-qa="mfa-remember"
            label={t('Remember me on this device')}
            tooltipLabel={t("Don't require MFA for 30 days from this browser")}
          />
          <LinkButton
            noSpace
            variant="text"
            color="blue"
            className="Login-help"
            onClick={onClickSendEmail}
            disabled={sendingEmail}
          >
            {mfaOption === 'email' ? t('Resend Email') : t('Email Code')}
          </LinkButton>
          <div className="Authform-actions">
            <TextButton
              noSpace
              variant="text"
              color="blue"
              className="Login-help"
              onClick={() => {
                setEmail('');
                setPassword('');
                setMfaToken('');
                setMfaRemember(false);
                setMfaOption(null);
              }}
            >
              {t('Change User')}
            </TextButton>
            <Button
              noSpace
              onClick={fetchSubmit}
              disabled={submitState!.loading || !validToken(mfaToken)}
            >
              {t('Login')}
            </Button>
          </div>
        </div>
      </div>
    </AuthForm>
  );
};
